import { useCallback, useEffect, useRef, useState } from 'react';
import { Alert, Box, Button, ButtonBase, CircularProgress, Stack, TextField, Tooltip, Typography } from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import useApi from 'hooks/useApi';
import { diApi } from '../api';
import type {
  CatalogueField,
  IntegrationLookups,
  IntegrationProcess,
  PipelineDetail,
  PipelineSpec,
  RecordAction,
  SourceEntity
} from '../types';
import type { StepProps } from './stepProps';
import StateChip from '../components/StateChip';
import PipelineRunControl from '../components/PipelineRunControl';
import SampleRail from './SampleRail';
import TriggerStep from './steps/TriggerStep';
import FilterStep from './steps/FilterStep';
import TargetStep from './steps/TargetStep';
import MappingStep from './steps/MappingStep';
import AutomationStep from './steps/AutomationStep';
import TestPublishStep from './steps/TestPublishStep';
import { countConditions, entityNoun, errorMessage, issuesAt } from './ConditionBuilder';

const STEPS = ['Trigger', 'Filter', 'Target', 'Mapping', 'Automation', 'Test & publish'];
/** Which spec paths each step owns — drives the stepper's error/warning colouring. */
const STEP_PATHS: string[][] = [['trigger'], ['filter'], ['target'], ['bindings'], ['identity', 'sync', 'automation', 'guards'], []];
const ACTION_PAST: Record<RecordAction, string> = { create: 'created', update: 'updated', delete: 'deleted' };
const SAVE_DEBOUNCE_MS = 800;

type SaveState = 'idle' | 'saving' | 'saved' | 'error';

const cap = (s: string) => s.charAt(0).toUpperCase() + s.slice(1);

/** Fill any part of the spec an older/blank draft may lack, so steps can read it without guards. */
function normaliseSpec(s: Partial<PipelineSpec> | null | undefined, entity: SourceEntity): PipelineSpec {
  return {
    ...(s as PipelineSpec),
    version: 1,
    trigger: { entity: s?.trigger?.entity ?? entity, actions: s?.trigger?.actions ?? ['create', 'update', 'delete'] },
    filter: s?.filter ?? { kind: 'group', op: 'AND', children: [] },
    target: s?.target ?? { mode: 'SINGLE', teamUid: '' },
    bindings: s?.bindings ?? [],
    identity: s?.identity ?? { keyField: 'id', adopt: null },
    sync: s?.sync ?? { defaultOnUpdate: 'FILL_EMPTY', onDelete: 'CANCEL', missingRequired: 'CREATE_AND_FLAG' },
    automation: s?.automation ?? [],
    guards: s?.guards ?? { maxCreatesPerHour: 60, pauseOnErrorRatePct: 20 }
  };
}

export default function PipelineEditor({
  clientUid,
  pipelineUid,
  onBack,
  onChanged
}: {
  clientUid: string;
  pipelineUid: string;
  onBack: () => void;
  onChanged?: () => void;
}) {
  const theme = useTheme();
  const { showToast } = useApi();
  // ApiContext rebuilds its value every render — keep the helper in a ref, never in effect deps.
  const toastRef = useRef(showToast);
  toastRef.current = showToast;
  const onChangedRef = useRef(onChanged);
  onChangedRef.current = onChanged;

  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [pipeline, setPipeline] = useState<PipelineDetail | null>(null);
  const [spec, setSpec] = useState<PipelineSpec | null>(null);
  const [name, setName] = useState('');
  const [processes, setProcesses] = useState<IntegrationProcess[]>([]);
  const [lookups, setLookups] = useState<IntegrationLookups>({ resourceTypes: [], roles: [] });
  const [catalogue, setCatalogue] = useState<CatalogueField[]>([]);
  const [step, setStep] = useState(0);
  const [sampleRecordId, setSampleRecordId] = useState<string | null>(null);
  const [saveState, setSaveState] = useState<SaveState>('idle');
  const [editedSinceTest, setEditedSinceTest] = useState(false);

  const specRef = useRef<PipelineSpec | null>(null);
  const nameRef = useRef('');
  const pipelineRef = useRef<PipelineDetail | null>(null);
  pipelineRef.current = pipeline;
  const dirtyRef = useRef(false);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const inflightRef = useRef<Promise<boolean> | null>(null);
  const mountedRef = useRef(true);

  /* ------------------------------ load ------------------------------ */
  useEffect(() => {
    let alive = true;
    setLoading(true);
    setLoadError(null);
    Promise.all([diApi.getPipeline(clientUid, pipelineUid), diApi.getProcesses(clientUid), diApi.getLookups(clientUid)])
      .then(([p, procs, lk]) => {
        if (!alive) return;
        const detail = p.data;
        const s = normaliseSpec(detail.draftSpec, detail.entity);
        setPipeline(detail);
        setSpec(s);
        specRef.current = s;
        setName(detail.name);
        nameRef.current = detail.name;
        setProcesses(procs.data || []);
        setLookups(lk.data || { resourceTypes: [], roles: [] });
        setSaveState('idle');
        dirtyRef.current = false;
        diApi
          .getCatalogue(clientUid, detail.sourceUid, detail.entity)
          .then((c) => {
            if (alive) setCatalogue(c.data || []);
          })
          .catch((e) => {
            if (alive) toastRef.current(errorMessage(e, 'Could not load the field catalogue'), 'warning');
          });
      })
      .catch((e) => {
        if (alive) setLoadError(errorMessage(e, 'Could not load this pipeline'));
      })
      .finally(() => {
        if (alive) setLoading(false);
      });
    return () => {
      alive = false;
    };
  }, [clientUid, pipelineUid]);

  /* ------------------------------ autosave ------------------------------ */
  const saveNow = useCallback(async (): Promise<boolean> => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
    // Serialise saves so an older body can never land after a newer one.
    while (inflightRef.current) await inflightRef.current;
    if (!dirtyRef.current || !specRef.current) return true;
    dirtyRef.current = false;
    const trimmed = nameRef.current.trim();
    const body = { spec: specRef.current, ...(trimmed ? { name: trimmed } : {}) };
    const run = (async () => {
      if (mountedRef.current) setSaveState('saving');
      try {
        const res = await diApi.saveDraft(clientUid, pipelineUid, body);
        // Keep the latest pipeline (draftValidation etc.) but NOT its spec — local edits may be newer.
        if (mountedRef.current) {
          setPipeline(res.data);
          setSaveState(dirtyRef.current ? 'saving' : 'saved');
        }
        onChangedRef.current?.();
        return true;
      } catch (e) {
        dirtyRef.current = true;
        if (mountedRef.current) {
          setSaveState('error');
          toastRef.current(errorMessage(e, 'Could not save the draft'), 'error');
        }
        return false;
      }
    })();
    inflightRef.current = run;
    const ok = await run;
    if (inflightRef.current === run) inflightRef.current = null;
    return ok;
  }, [clientUid, pipelineUid]);

  const saveNowRef = useRef(saveNow);
  saveNowRef.current = saveNow;

  const scheduleSave = useCallback(() => {
    dirtyRef.current = true;
    setSaveState('saving');
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => {
      timerRef.current = null;
      void saveNowRef.current();
    }, SAVE_DEBOUNCE_MS);
  }, []);

  // Leaving the editor with a pending edit: save it rather than drop it.
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      if (timerRef.current) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }
      if (dirtyRef.current) void saveNowRef.current();
    };
  }, []);

  const onChange = useCallback(
    (next: PipelineSpec) => {
      specRef.current = next;
      setSpec(next);
      setEditedSinceTest(true);
      scheduleSave();
    },
    [scheduleSave]
  );

  const onNameChange = (v: string) => {
    setName(v);
    nameRef.current = v;
    scheduleSave();
  };

  /** Save immediately; resolves when the draft is on the server, rejects if the save failed. */
  const flushDraft = useCallback(async () => {
    const ok = await saveNow();
    if (!ok) throw new Error('Could not save the draft');
  }, [saveNow]);

  const onPipelineUpdated = useCallback((p: PipelineDetail) => {
    const prev = pipelineRef.current;
    if (p.lastTest && (p.lastTest.uid !== prev?.lastTest?.uid || p.lastTest.ranAt !== prev?.lastTest?.ranAt)) setEditedSinceTest(false);
    setPipeline(p);
    onChangedRef.current?.();
  }, []);

  const handleBack = async () => {
    const ok = await saveNow();
    if (ok) onBack();
  };

  /* ------------------------------ render ------------------------------ */
  if (loading) {
    return (
      <Box sx={{ display: 'grid', placeItems: 'center', py: 8 }}>
        <CircularProgress size={28} />
      </Box>
    );
  }
  if (loadError || !pipeline || !spec) {
    return (
      <Stack spacing={2} alignItems="flex-start">
        <Button onClick={onBack} sx={{ textTransform: 'none', color: 'text.secondary', px: 0 }}>
          ← All pipelines
        </Button>
        <Alert severity="error">{loadError ?? 'Could not load this pipeline'}</Alert>
      </Stack>
    );
  }

  const issues = pipeline.draftValidation ?? [];
  const [, many] = entityNoun(spec.trigger.entity);

  const statusFor = (i: number): string => {
    switch (i) {
      case 0: {
        const a = spec.trigger.actions ?? [];
        if (!a.length) return `${cap(many)} · no changes`;
        if (a.length === 3) return `${cap(many)} · every change`;
        return `${cap(many)} · ${a.map((x) => ACTION_PAST[x]).join(', ')}`;
      }
      case 1: {
        const n = countConditions(spec.filter);
        return n ? `${n} condition${n === 1 ? '' : 's'}` : 'No conditions';
      }
      case 2: {
        const t = spec.target;
        if (t.mode === 'SINGLE') return t.teamUid ? processes.find((p) => p.uid === t.teamUid)?.name ?? 'Unknown process' : 'Not chosen';
        const routed = (t.routes ?? []).filter((r) => r.teamUid).length;
        return `Routed · ${routed} of ${(t.routes ?? []).length}`;
      }
      case 3: {
        const src = spec.bindings.filter((b) => b.owner === 'SOURCE').length;
        return spec.bindings.length ? `${src} Aljex · ${spec.bindings.length - src} Ops` : 'Nothing mapped';
      }
      case 4: {
        const n = spec.automation.length;
        return n ? `${n} rule${n === 1 ? '' : 's'}` : 'No rules';
      }
      default: {
        const t = pipeline.lastTest;
        if (!t) return 'Not tested';
        if (editedSinceTest) return 'Changed · test again';
        const n = (t.issues ?? []).filter((x) => x.level !== 'ok').length;
        return n ? `Tested · ${n} to check` : 'Tested · all clear';
      }
    }
  };

  const toneFor = (i: number): { color?: string; tip: string } => {
    const own = issuesAt(issues, STEP_PATHS[i]);
    const errors = own.filter((x) => x.level === 'error');
    if (errors.length) return { color: theme.palette.error.main, tip: own.map((x) => x.message).join(' · ') };
    if (own.length) return { color: theme.palette.warning.dark, tip: own.map((x) => x.message).join(' · ') };
    if (i === 5) {
      if (pipeline.lastTest && editedSinceTest)
        return { color: theme.palette.warning.dark, tip: 'You changed the pipeline after the last test.' };
      if (pipeline.lastTest && !(pipeline.lastTest.issues ?? []).some((x) => x.level !== 'ok'))
        return { color: theme.palette.success.main, tip: '' };
    }
    return { tip: '' };
  };

  const versionMeta =
    pipeline.liveVersion != null
      ? `v${pipeline.liveVersion} ${
          pipeline.state === 'SHADOW' || pipeline.state === 'LIVE' ? 'running' : pipeline.state === 'PAUSED' ? 'paused' : 'published'
        } · editing draft v${pipeline.draftVersion}`
      : `Draft v${pipeline.draftVersion}`;

  const saveText = saveState === 'saving' ? 'Saving…' : saveState === 'saved' ? 'Saved' : saveState === 'error' ? 'Not saved' : '';

  const stepProps: StepProps = { clientUid, pipeline, spec, onChange, processes, lookups, catalogue, sampleRecordId };
  const renderStep = () => {
    switch (step) {
      case 0:
        return <TriggerStep {...stepProps} />;
      case 1:
        return <FilterStep {...stepProps} />;
      case 2:
        return <TargetStep {...stepProps} />;
      case 3:
        return <MappingStep {...stepProps} />;
      case 4:
        return <AutomationStep {...stepProps} />;
      default:
        return <TestPublishStep {...stepProps} flushDraft={flushDraft} onPipelineUpdated={onPipelineUpdated} />;
    }
  };

  return (
    <Stack spacing={1.75}>
      {/* Header */}
      <Stack direction="row" alignItems="center" flexWrap="wrap" sx={{ columnGap: 1.75, rowGap: 1.25 }}>
        <Button onClick={handleBack} sx={{ textTransform: 'none', color: 'text.secondary', px: 0.5, minWidth: 0, fontWeight: 400 }}>
          ← All pipelines
        </Button>
        <Stack direction="row" alignItems="center" flexWrap="wrap" sx={{ columnGap: 1.25, rowGap: 0.75, minWidth: 0 }}>
          <TextField
            variant="standard"
            value={name}
            onChange={(e) => onNameChange(e.target.value)}
            placeholder="Pipeline name"
            InputProps={{ disableUnderline: false, sx: { fontSize: 18, fontWeight: 600 } }}
            inputProps={{ 'aria-label': 'Pipeline name', size: Math.max(12, name.length + 1) }}
          />
          <StateChip state={pipeline.state} />
          <Typography variant="caption" color="text.secondary">
            {versionMeta}
          </Typography>
          {saveText && (
            <Typography variant="caption" sx={{ color: saveState === 'error' ? 'error.main' : 'text.secondary' }} aria-live="polite">
              {saveText}
              {saveState === 'error' && (
                <Button size="small" onClick={() => void saveNow()} sx={{ ml: 0.5, py: 0, minWidth: 0, textTransform: 'none' }}>
                  Retry
                </Button>
              )}
            </Typography>
          )}
        </Stack>
        <Stack direction="row" spacing={1} sx={{ ml: 'auto' }}>
          <PipelineRunControl
            clientUid={clientUid}
            pipeline={{ uid: pipeline.uid, name: name || pipeline.name, state: pipeline.state }}
            onChanged={onPipelineUpdated}
          />
          <Button variant="outlined" color="secondary" onClick={() => setStep(5)}>
            Run test
          </Button>
          <Button variant="contained" onClick={() => setStep(5)}>
            Publish…
          </Button>
        </Stack>
      </Stack>

      {pipeline.state === 'NEEDS_ATTENTION' && pipeline.healthMessage && <Alert severity="error">{pipeline.healthMessage}</Alert>}

      {/* Stepper */}
      <Box
        component="ol"
        aria-label="Pipeline steps"
        sx={{
          listStyle: 'none',
          m: 0,
          p: 0,
          display: 'grid',
          gridTemplateColumns: 'repeat(6, minmax(0, 1fr))',
          gap: '1px',
          bgcolor: 'divider',
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: '10px',
          overflow: 'hidden'
        }}
      >
        {STEPS.map((label, i) => {
          const current = i === step;
          const tone = toneFor(i);
          return (
            <Box component="li" key={label} sx={{ display: 'flex' }}>
              <Tooltip title={tone.tip}>
                <ButtonBase
                  onClick={() => setStep(i)}
                  aria-current={current ? 'step' : undefined}
                  sx={{
                    width: '100%',
                    justifyContent: 'flex-start',
                    textAlign: 'left',
                    bgcolor: current ? alpha(theme.palette.primary.main, 0.08) : 'background.paper',
                    px: 1.5,
                    py: 1.375,
                    display: 'grid',
                    gridTemplateColumns: 'auto minmax(0, 1fr)',
                    columnGap: 1.125,
                    rowGap: '1px',
                    alignItems: 'center',
                    '&:hover': { bgcolor: current ? alpha(theme.palette.primary.main, 0.12) : 'grey.50' }
                  }}
                >
                  <Box
                    sx={{
                      gridRow: 'span 2',
                      width: 24,
                      height: 24,
                      borderRadius: '50%',
                      display: 'grid',
                      placeItems: 'center',
                      fontSize: 12,
                      fontWeight: 600,
                      bgcolor: current ? 'primary.main' : 'grey.50',
                      color: current ? 'primary.contrastText' : 'text.secondary',
                      border: '1px solid',
                      borderColor: current ? 'primary.main' : tone.color ?? 'divider'
                    }}
                  >
                    {i + 1}
                  </Box>
                  <Typography sx={{ fontWeight: 600, fontSize: 13, color: 'text.primary' }}>{label}</Typography>
                  <Typography noWrap sx={{ fontSize: 12, color: tone.color ?? 'text.secondary' }}>
                    {statusFor(i)}
                  </Typography>
                </ButtonBase>
              </Tooltip>
            </Box>
          );
        })}
      </Box>

      {/* Workspace: canvas + sample rail */}
      <Box sx={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) 320px', gap: 2, alignItems: 'start' }}>
        <Box
          sx={{
            bgcolor: 'background.paper',
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: '10px',
            p: 2.75,
            display: 'grid',
            gap: 2.25,
            gridTemplateColumns: 'minmax(0, 1fr)',
            minWidth: 0
          }}
        >
          {renderStep()}
          <Stack direction="row" spacing={1} sx={{ borderTop: '1px solid', borderColor: 'divider', pt: 2 }}>
            <Button variant="outlined" color="secondary" disabled={step === 0} onClick={() => setStep((s) => Math.max(0, s - 1))}>
              Back
            </Button>
            <Box flex={1} />
            {step < STEPS.length - 1 && (
              <Button variant="contained" onClick={() => setStep((s) => Math.min(STEPS.length - 1, s + 1))}>
                Next
              </Button>
            )}
          </Stack>
        </Box>

        <SampleRail
          clientUid={clientUid}
          pipelineUid={pipelineUid}
          sourceUid={pipeline.sourceUid}
          entity={spec.trigger.entity ?? pipeline.entity}
          spec={spec}
          recordId={sampleRecordId}
          onRecordChange={setSampleRecordId}
        />
      </Box>
    </Stack>
  );
}
